import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ApiError, api } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import {
  Badge, Button, EmptyState, ErrorNote, Field, Input, Modal, PageLoader, Panel, Textarea,
} from '@/components/ui'

const ROLE_TONE = { dm: 'gold', player: 'arcane', guest: 'neutral' } as const

export default function Campaigns() {
  const navigate = useNavigate()
  const { data, error, loading, reload } = useAsync(() => api.listCampaigns(), [])
  const [creating, setCreating] = useState(false)
  const [joinOpen, setJoinOpen] = useState(false)

  if (loading) return <PageLoader />

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-display">Your campaigns</h1>
          <p className="mt-3 text-[15px] text-ink-400">Tables you run, and tables you play at.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setJoinOpen(true)}>Enter a code</Button>
          <Button variant="primary" onClick={() => setCreating(true)}>New campaign</Button>
        </div>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {data && data.campaigns.length === 0 ? (
        <EmptyState
          title="No campaigns yet"
          description="Start one as DM, or enter an invite code from the DM running your table."
          action={<Button variant="primary" onClick={() => setCreating(true)}>Create your first campaign</Button>}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data?.campaigns.map((campaign) => (
            <Link key={campaign.id} to={`/campaigns/${campaign.id}`} className="group">
              <Panel className="panel-interactive h-full p-5">
                <div className="flex items-start justify-between gap-3">
                  <h2 className="font-display text-lg leading-tight text-ink-100 group-hover:text-gold-400">
                    {campaign.name}
                  </h2>
                  <Badge tone={ROLE_TONE[campaign.role]}>{campaign.role}</Badge>
                </div>

                {campaign.blurb && (
                  <p className="mt-2 line-clamp-2 text-sm text-ink-400">{campaign.blurb}</p>
                )}

                <dl className="mt-4 flex gap-4 border-t border-ink-700/60 pt-3 text-xs text-ink-500">
                  <div><dt className="inline">DM </dt><dd className="inline text-ink-300">{campaign.dmName}</dd></div>
                  <div className="ml-auto flex gap-3 font-mono">
                    <span>{campaign.memberCount} at the table</span>
                    <span>{campaign.characterCount} PCs</span>
                  </div>
                </dl>
              </Panel>
            </Link>
          ))}
        </div>
      )}

      <NewCampaignModal open={creating} onClose={() => setCreating(false)} onCreated={reload} />
      <JoinCodeModal open={joinOpen} onClose={() => setJoinOpen(false)} onSubmit={(code) => navigate(`/join/${code}`)} />
    </div>
  )
}

function NewCampaignModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [blurb, setBlurb] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const res = await api.createCampaign({ name: name.trim(), blurb: blurb.trim() })
      onCreated()
      onClose()
      setName(''); setBlurb('')
      navigate(`/campaigns/${res.campaign.id}`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the campaign')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} title="New campaign" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name">
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} placeholder="The Sundered Coast" />}
        </Field>
        <Field label="Blurb" hint="One line your players see on their campaign list.">
          {(id) => <Textarea id={id} value={blurb} onChange={(e) => setBlurb(e.target.value)} maxLength={500} placeholder="A salt-worn frontier where the old gods still collect debts." />}
        </Field>
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" busy={busy} disabled={!name.trim()}>Create</Button>
        </div>
      </form>
    </Modal>
  )
}

function JoinCodeModal({ open, onClose, onSubmit }: { open: boolean; onClose: () => void; onSubmit: (code: string) => void }) {
  const [code, setCode] = useState('')
  return (
    <Modal open={open} title="Join a campaign" onClose={onClose}>
      <form
        onSubmit={(event) => { event.preventDefault(); onSubmit(code.trim().toUpperCase()) }}
        className="space-y-4"
      >
        <Field label="Invite code" hint="The DM can generate one from their campaign page.">
          {(id) => (
            <Input id={id} value={code} onChange={(e) => setCode(e.target.value)} required
              placeholder="ABCD-2345" className="font-mono uppercase tracking-widest" />
          )}
        </Field>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={!code.trim()}>Continue</Button>
        </div>
      </form>
    </Modal>
  )
}
